import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Trophy, Medal, Award, X } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACE_META = {
  1: { label: '1st', title: 'Gold Trophy', icon: Trophy, color: 'text-yellow-300', bg: 'bg-yellow-500/15 border-yellow-500/40' },
  2: { label: '2nd', title: 'Silver Trophy', icon: Medal, color: 'text-zinc-200', bg: 'bg-zinc-300/15 border-zinc-300/40' },
  3: { label: '3rd', title: 'Bronze Trophy', icon: Award, color: 'text-amber-500', bg: 'bg-amber-700/15 border-amber-700/40' },
};

/**
 * Modal for admin/host to manually assign 1st / 2nd / 3rd place trophies
 * on a completed tournament. Overrides the auto-awarded ties.
 */
export function DesignatePlacementsModal({ open, onOpenChange, tournament, adminId, onSuccess }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Map of place -> member_id (single winner per place for MVP)
  const [assignments, setAssignments] = useState({ 1: null, 2: null, 3: null });

  useEffect(() => {
    if (!open || !tournament?.id) return;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/tournaments/${tournament.id}/leaderboard`);
        setLeaderboard(res.data || []);
        // Prefill from existing placements if the tournament already has them
        if (tournament.placements?.length) {
          const pre = { 1: null, 2: null, 3: null };
          for (const p of tournament.placements) {
            if (!pre[p.place]) pre[p.place] = p.member_id;
          }
          setAssignments(pre);
        } else {
          // Default from current leaderboard ordering (first 3 unique players)
          const pre = { 1: null, 2: null, 3: null };
          (res.data || []).slice(0, 3).forEach((row, idx) => {
            pre[idx + 1] = row.member_id;
          });
          setAssignments(pre);
        }
      } catch {
        toast.error('Failed to load leaderboard');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, tournament?.id, tournament?.placements]);

  const takenIds = useMemo(
    () => new Set(Object.values(assignments).filter(Boolean)),
    [assignments]
  );

  const setPlace = (place, memberId) => {
    setAssignments((prev) => {
      const next = { ...prev };
      // If this member is currently in another place, clear that first
      for (const p of [1, 2, 3]) {
        if (next[p] === memberId) next[p] = null;
      }
      next[place] = memberId;
      return next;
    });
  };

  const clearPlace = (place) => setAssignments((prev) => ({ ...prev, [place]: null }));

  const handleSave = async () => {
    const placements = [1, 2, 3]
      .filter((p) => assignments[p])
      .map((p) => ({ member_id: assignments[p], place: p }));

    if (placements.length === 0) {
      toast.error('Assign at least a 1st place');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/admin/tournaments/${tournament.id}/designate-placements`,
        { admin_id: adminId, placements }
      );
      toast.success('Placements updated');
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save placements');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-white/10 max-w-lg" data-testid="designate-placements-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-300" />
            Designate Winners
          </DialogTitle>
          <DialogDescription>
            Pick the players who take 1st, 2nd, and 3rd. Existing trophy badges for this tournament will be replaced.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-muted-foreground text-sm py-8 text-center">Loading leaderboard…</p>
        ) : leaderboard.length === 0 ? (
          <p className="text-muted-foreground text-sm py-8 text-center">No participants with scores yet.</p>
        ) : (
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            {[1, 2, 3].map((place) => {
              const meta = PLACE_META[place];
              const Icon = meta.icon;
              const currentId = assignments[place];
              return (
                <div key={place} className={`rounded-xl border p-3 ${meta.bg}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-5 h-5 ${meta.color}`} />
                      <span className="text-white font-medium">
                        {meta.label} Place — {meta.title}
                      </span>
                    </div>
                    {currentId && (
                      <button
                        onClick={() => clearPlace(place)}
                        className="text-muted-foreground hover:text-white text-xs flex items-center gap-1"
                        data-testid={`clear-place-${place}`}
                      >
                        <X className="w-3 h-3" />
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-1.5 max-h-40 overflow-y-auto">
                    {leaderboard.map((row) => {
                      const isTaken = takenIds.has(row.member_id) && currentId !== row.member_id;
                      const isSelected = currentId === row.member_id;
                      return (
                        <button
                          key={row.member_id}
                          disabled={isTaken}
                          onClick={() => setPlace(place, row.member_id)}
                          className={`text-left px-3 py-2 rounded-md text-sm transition-all flex items-center justify-between ${
                            isSelected
                              ? 'bg-white/10 border border-white/30 text-white'
                              : isTaken
                              ? 'bg-transparent border border-white/5 text-muted-foreground/40 opacity-50 cursor-not-allowed'
                              : 'bg-transparent border border-white/10 text-muted-foreground hover:text-white hover:border-white/30'
                          }`}
                          data-testid={`assign-place-${place}-${row.member_id}`}
                        >
                          <span className="truncate">{row.member_name}</span>
                          <span className="font-mono text-xs opacity-70 shrink-0 ml-2">
                            {row.score_label || '—'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            className="bg-secondary text-black hover:bg-secondary/90"
            data-testid="designate-save-btn"
          >
            {saving ? 'Saving…' : 'Save Placements'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DesignatePlacementsModal;
